import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import EmailAdmin from './EmailAdmin';
import EmailConfigAdmin from './EmailConfigAdmin';
import MenuOptionsAdmin from './MenuOptionsAdmin';
import PDFFormularAdmin from './PDFFormularAdmin';
import OrderOptionsAdmin from './OrderOptionsAdmin';
import AnalyticsView from './AnalyticsView';

const {
  FiMail, FiSettings, FiMenu, FiArrowLeft, FiLogOut, FiShield, FiUser, 
  FiAlertTriangle, FiFileText, FiTruck, FiBarChart2
} = FiIcons;

const AdminPanel = ({ onLogout }) => {
  const [activeSection, setActiveSection] = useState('menu'); // default to menu options

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'email':
        return <EmailAdmin />;
      case 'emailConfig':
        return <EmailConfigAdmin />;
      case 'menu':
        return <MenuOptionsAdmin />;
      case 'pdfFormular':
        return <PDFFormularAdmin />;
      case 'orderOptions':
        return <OrderOptionsAdmin />;
      case 'analytics':
        return <AnalyticsView />;
      default:
        return <MenuOptionsAdmin />;
    }
  };

  const handleLogout = () => {
    if (confirm('Möchten Sie sich wirklich vom Admin-Bereich abmelden?')) {
      onLogout();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b shadow-sm p-4">
        <div className="container mx-auto flex justify-between items-center">
          <h1 className="text-xl font-bold text-orange-600 flex items-center">
            <SafeIcon icon={FiShield} className="mr-2" />
            🍔 Wunsch-Burger Admin
          </h1>
          <div className="flex space-x-2">
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex items-center"
            >
              <SafeIcon icon={FiLogOut} className="mr-2" />
              Abmelden
            </button>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center"
            >
              <SafeIcon icon={FiArrowLeft} className="mr-2" />
              Zurück zur App
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto py-6">
        {/* Admin info banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start">
          <SafeIcon icon={FiUser} className="text-blue-500 mt-1 mr-3 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-blue-800">Administrator-Bereich</h3>
            <p className="text-blue-700 text-sm mt-1">
              Sie sind als Administrator angemeldet und haben vollen Zugriff auf alle Einstellungen. Bitte gehen Sie mit diesen Berechtigungen verantwortungsvoll um.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
          <div className="flex border-b overflow-x-auto">
            <button
              className={`px-6 py-3 flex items-center whitespace-nowrap ${
                activeSection === 'menu'
                  ? 'bg-orange-50 text-orange-600 border-b-2 border-orange-500'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setActiveSection('menu')}
            >
              <SafeIcon icon={FiMenu} className="mr-2" />
              Menü-Optionen
            </button>
            <button
              className={`px-6 py-3 flex items-center whitespace-nowrap ${
                activeSection === 'orderOptions'
                  ? 'bg-orange-50 text-orange-600 border-b-2 border-orange-500'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setActiveSection('orderOptions')}
            >
              <SafeIcon icon={FiTruck} className="mr-2" />
              Bestelloptionen
            </button>
            <button
              className={`px-6 py-3 flex items-center whitespace-nowrap ${
                activeSection === 'pdfFormular'
                  ? 'bg-orange-50 text-orange-600 border-b-2 border-orange-500'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setActiveSection('pdfFormular')}
            >
              <SafeIcon icon={FiFileText} className="mr-2" />
              PDF Formular
            </button>
            <button
              className={`px-6 py-3 flex items-center whitespace-nowrap ${
                activeSection === 'analytics'
                  ? 'bg-orange-50 text-orange-600 border-b-2 border-orange-500'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setActiveSection('analytics')}
            >
              <SafeIcon icon={FiBarChart2} className="mr-2" />
              Analytics
            </button>
            <button
              className={`px-6 py-3 flex items-center whitespace-nowrap ${
                activeSection === 'email'
                  ? 'bg-orange-50 text-orange-600 border-b-2 border-orange-500'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setActiveSection('email')}
            >
              <SafeIcon icon={FiMail} className="mr-2" />
              E-Mail Verwaltung
            </button>
            <button
              className={`px-6 py-3 flex items-center whitespace-nowrap ${
                activeSection === 'emailConfig'
                  ? 'bg-orange-50 text-orange-600 border-b-2 border-orange-500'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setActiveSection('emailConfig')}
            >
              <SafeIcon icon={FiSettings} className="mr-2" />
              E-Mail Konfiguration
            </button>
          </div>
        </div>

        {/* Security warning */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <SafeIcon icon={FiAlertTriangle} className="text-yellow-500 mt-1 mr-3 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-yellow-800">Sicherheitshinweis</h3>
              <p className="text-yellow-700 text-sm mt-1">
                Vergessen Sie nicht, sich abzumelden, wenn Sie den Admin-Bereich verlassen, besonders wenn Sie einen öffentlichen oder gemeinsam genutzten Computer verwenden.
              </p>
            </div>
          </div>
        </div>

        {renderActiveSection()}
      </div>
    </div>
  );
};

export default AdminPanel;